import { type FC, type ComponentProps, useRef, useCallback, useEffect, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { HpSharedShopByModuleControl } from './HpSharedShopByModuleControl';
import { HpSharedShopByModuleVariant } from './HpSharedShopByModuleVariant';

import {
    isTrendingCreatorsVariant,
    TRENDING_CREATORS_TEST_NAME,
} from '../helpers/abTestTrendingCreators';
import { type HpSharedShopByModule_componentModule$key } from './__generated__/HpSharedShopByModule_componentModule.graphql';
import { type HpSharedShopByModule_viewer$key } from './__generated__/HpSharedShopByModule_viewer.graphql';

const componentModuleFragment = graphql`
    fragment HpSharedShopByModule_componentModule on ShopByModule {
        ...HpSharedShopByModuleControl_componentModule @skip(if: $isTrendingCreatorsVariant)
    }
`;

const viewerFragment = graphql`
    fragment HpSharedShopByModule_viewer on Viewer {
        ...HpSharedShopByModuleVariant_viewer @include(if: $isTrendingCreatorsVariant)
    }
`;

export const HpSharedShopByModule: FC<{
    viewer: HpSharedShopByModule_viewer$key;
    componentModule: HpSharedShopByModule_componentModule$key;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
    isClient: boolean;
}> = ({
    viewer: viewerRef,
    componentModule: componentModuleRef,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
    isClient,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const elementRef = useRef<HTMLDivElement>(null);
    const [isVariant, setIsVariant] = useState<boolean | null>(null);

    useEffect(() => {
        setIsVariant(isTrendingCreatorsVariant());
    }, []);

    const onVisibilityChange: ComponentProps<typeof VisibilityTracker>['onVisibilityChange'] =
        useCallback(({ isVisible }) => {
            if (isVisible) {
                trackAbTestV2Variant(TRENDING_CREATORS_TEST_NAME);
            }
        }, []);

    //isClient ensures that the module is rendered only after client side refetch
    if (!isClient || isVariant === null) {
        return null;
    }

    return (
        <div ref={elementRef}>
            <VisibilityTracker elementRef={elementRef} onVisibilityChange={onVisibilityChange} />
            {isVariant ? (
                <HpSharedShopByModuleVariant viewer={viewer} />
            ) : (
                <HpSharedShopByModuleControl
                    componentModule={componentModule}
                    useLazyLoadImages={useLazyLoadImages}
                    moduleIndex={moduleIndex}
                    totalModules={totalModules}
                />
            )}
        </div>
    );
};
